import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  Ban,
  Check,
  CheckCircle2,
  Clock,
  FileLock2,
  RefreshCw,
  ShieldAlert,
  X,
  XCircle,
} from 'lucide-react';
import {
  fetchApprovals,
  approveApproval,
  rejectApproval,
  type ApprovalItem,
} from '../lib/api';

type ApprovalType = ApprovalItem['type'];

const typeConfig: Record<
  ApprovalType,
  { icon: React.ElementType; label: string; color: string }
> = {
  tax_filing: { icon: FileLock2, label: 'Tax filing', color: 'text-zt-info' },
  crypto_transfer: { icon: AlertTriangle, label: 'Crypto transfer', color: 'text-zt-warning' },
  destructive_action: { icon: ShieldAlert, label: 'Destructive action', color: 'text-zt-danger' },
  retention_change: { icon: Clock, label: 'Retention change', color: 'text-zt-muted' },
  gateway_settlement: { icon: RefreshCw, label: 'Gateway settlement', color: 'text-zt-warning' },
  uncertain_match: { icon: RefreshCw, label: 'Uncertain match', color: 'text-zt-warning' },
};

const riskConfig: Record<ApprovalItem['riskLevel'], string> = {
  low: 'bg-zt-primary/10 text-zt-primary',
  medium: 'bg-zt-warning/10 text-zt-warning',
  high: 'bg-orange-500/10 text-orange-500',
  critical: 'bg-zt-danger/10 text-zt-danger',
};

const statusConfig: Record<ApprovalItem['status'], { icon: React.ElementType; color: string }> = {
  pending: { icon: Clock, color: 'text-zt-warning' },
  approved: { icon: CheckCircle2, color: 'text-zt-primary' },
  rejected: { icon: XCircle, color: 'text-zt-danger' },
  expired: { icon: Ban, color: 'text-zt-muted' },
};

interface ApprovalQueueProps {
  compact?: boolean;
}

function ApprovalQueue({ compact }: ApprovalQueueProps) {
  const [items, setItems] = useState<ApprovalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const result = await fetchApprovals();
    if (result.ok && result.data) {
      setItems(result.data.items ?? []);
    } else {
      setItems([]);
      setError(result.error?.message ?? 'Could not load approvals');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleApprove = async (id: string) => {
    setActing((prev) => new Set(prev).add(id));
    const result = await approveApproval(id);
    if (result.ok) {
      setItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, status: 'approved' } : item))
      );
    }
    setActing((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleReject = async (id: string) => {
    setActing((prev) => new Set(prev).add(id));
    const result = await rejectApproval(id);
    if (result.ok) {
      setItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, status: 'rejected' } : item))
      );
    }
    setActing((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const pendingCount = items.filter((i) => i.status === 'pending').length;
  const visibleItems = compact ? items.filter((i) => i.status === 'pending').slice(0, 4) : items;

  if (loading) {
    return (
      <div className="rounded-xl border border-zt-border bg-zt-surface p-6">
        <div className="mb-4 h-5 w-1/3 animate-pulse rounded bg-zt-border" />
        <div className="space-y-3">
          {[1, 2, 3].map((n) => (
            <div key={n} className="h-12 w-full animate-pulse rounded bg-zt-border" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-zt-border bg-zt-surface p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="text-zt-warning" size={20} />
          <h3 className="font-semibold text-white">Approval queue</h3>
          {pendingCount > 0 && (
            <span className="rounded-full bg-zt-danger/10 px-2 py-0.5 text-xs font-medium text-zt-danger">
              {pendingCount} pending
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-3 text-sm text-zt-danger">{error}</div>
      )}

      {visibleItems.length === 0 ? (
        <div className="flex items-center gap-2 py-4 text-sm text-zt-muted">
          <Check size={16} />
          Nothing pending approval.
        </div>
      ) : (
        <div className="space-y-3">
          {visibleItems.map((item) => {
            const config = typeConfig[item.type];
            const Icon = config.icon;
            const StatusIcon = statusConfig[item.status].icon;
            const isPending = item.status === 'pending';

            return (
              <div
                key={item.id}
                className="flex flex-col gap-3 rounded-lg border border-zt-border bg-zt-bg p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-md bg-zt-elevated p-1.5">
                    <Icon className={config.color} size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-slate-200">{item.title}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${riskConfig[item.riskLevel]}`}>
                        {item.riskLevel}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-zt-muted">
                        <StatusIcon size={14} className={statusConfig[item.status].color} />
                        {item.status}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-zt-muted">{item.description}</div>
                    <div className="mt-1 text-xs text-zt-muted">
                      Requested by {item.requestedBy} • {new Date(item.requestedAt).toLocaleString()}
                    </div>
                  </div>
                </div>

                {isPending ? (
                  <div className="flex items-center gap-2 sm:flex-col md:flex-row">
                    <button
                      type="button"
                      onClick={() => handleApprove(item.id)}
                      disabled={acting.has(item.id)}
                      className="flex items-center gap-1 rounded-lg bg-zt-primary/10 px-3 py-1.5 text-sm font-medium text-zt-primary hover:bg-zt-primary/20 disabled:opacity-50"
                    >
                      <Check size={16} />
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => handleReject(item.id)}
                      disabled={acting.has(item.id)}
                      className="flex items-center gap-1 rounded-lg bg-zt-danger/10 px-3 py-1.5 text-sm font-medium text-zt-danger hover:bg-zt-danger/20 disabled:opacity-50"
                    >
                      <X size={16} />
                      Reject
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-sm text-zt-muted">
                    {item.status === 'approved' ? (
                      <>
                        <CheckCircle2 size={16} className="text-zt-primary" />
                        Approved
                      </>
                    ) : item.status === 'expired' ? (
                      <>
                        <Ban size={16} className="text-zt-muted" />
                        Expired
                      </>
                    ) : (
                      <>
                        <Ban size={16} className="text-zt-danger" />
                        Rejected
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ApprovalQueue;
